import { axiosBaseQuery } from "@/shared/api/base-query";
import { insightsApiInstance } from "@/shared/api/axios";
import { createApi } from "@reduxjs/toolkit/query/react";
import { CameraType } from "../types";
import { processGondolaData } from "../helpers/gondola";

const baseQuery = axiosBaseQuery(insightsApiInstance);

export const insightsApi = createApi({
  reducerPath: "insightsApi",
  baseQuery,
  endpoints: (builder) => ({
    gondolas: builder.query<GondolasResponse, GondolasVariables>({
      query: (params) => ({
        url: `/gondolas`,
        method: "GET",
        params,
      }),
      transformResponse: (response: RawGondolasResponse) => ({
        results: processGondolaData(response.results),
        count: response.count,
      }),
    }),
    gondolasRaw: builder.query<RawGondolasResponse, GondolasVariables>({
      query: (params) => ({
        url: `/gondolas`,
        method: "GET",
        params,
      }),
    }),
    gondolasSkuSections: builder.query<
      GondolaSkuSectionsResponse,
      GondolaSkuSectionsVariables
    >({
      query: ({ gondolaId, ...params }) => ({
        url: `/gondolas/${gondolaId}/sku_sections`,
        method: "GET",
        params,
      }),
    }),

    planogramSchedules: builder.query<SchedulesResponse, SchedulesVariables>({
      query: (params) => ({
        url: `/planograms/schedules`,
        method: "GET",
        params,
      }),
    }),
    planogram: builder.query<PlanogramResponse, { id: number }>({
      query: ({ id }) => ({
        url: `/planograms/${id}`,
        method: "GET",
      }),
    }),

    zoneHistorySchedules: builder.query<SchedulesResponse, SchedulesVariables>({
      query: (params) => ({
        url: `/zone_histories/schedules`,
        method: "GET",
        params,
      }),
    }),

    zoneHistory: builder.query<ZoneHistoryResponse, ZoneHistoryVariables>({
      query: ({ historyId, ...params }) => ({
        url: `/zone_histories/${historyId}`,
        method: "GET",
        params,
      }),
    }),

    zoneProjection: builder.query<
      ZoneProjectionResponse,
      ZoneProjectionVariables
    >({
      query: ({ historyId, zoneId, cameraId, ...params }) => ({
        url: `/zone_histories/${historyId}/zones/${zoneId}/camera_projection/${cameraId}`,
        method: "GET",
        params,
      }),
    }),

    calibrationSchedules: builder.query<SchedulesResponse, SchedulesVariables>({
      query: () => ({
        url: `/calibrations/schedules`,
        method: "GET",
      }),
    }),

    calibrations: builder.query<
      CalibrationsResponse,
      { calibrationId: number }
    >({
      query: ({ calibrationId }) => ({
        url: `/calibrations/${calibrationId}`,
        method: "GET",
      }),
    }),

    variants: builder.query<VariantsResponse, VariantsVariables>({
      query: ({ page = 1, page_size = 100, ...params }) => ({
        url: `/variants`,
        method: "GET",
        params: {
          ...params,
          page,
          page_size,
        },
      }),
    }),
    getFrameAvailability: builder.query<
      FrameAvailabilityResponse,
      FrameAvailabilityQueryParams
    >({
      query: (params) => ({
        url: "/frame_found",
        method: "GET",
        params,
      }),
    }),
  }),
});

export const {
  useGondolasQuery,
  useGondolasRawQuery,
  useLazyGondolasRawQuery,
  useLazyGondolasQuery,
  useGondolasSkuSectionsQuery,
  usePlanogramSchedulesQuery,
  usePlanogramQuery,
  useZoneHistoryQuery,
  useZoneHistorySchedulesQuery,
  useZoneProjectionQuery,
  useLazyZoneProjectionQuery,
  useCalibrationSchedulesQuery,
  useCalibrationsQuery,
  useVariantsQuery,
  useLazyVariantsQuery,
  useGetFrameAvailabilityQuery,
  useLazyGetFrameAvailabilityQuery,
} = insightsApi;

export type AllCameraIdsResponse = {
  result: {
    camera_ids: string[];
  };
};

export type CameraSkuSection = {
  id: number;
  variant: {
    id: number;
    name: string;
    barcode: string;
    image: string;
  };
  polygon_points: [number, number][];
};

type GondolasVariables = {
  search?: string;
  page?: number;
  limit?: number;
  ordering?: string;
  on_datetime?: string;
};

export type RawCameraDetails = {
  camera_id: string;
  thumbnail: string;
  image_png: string;
  image_webp: string;
  height: number;
  width: number;
  camera_type: CameraType;
};

export type RawGondola = {
  id: number;
  name: string;
  cameras: RawCameraDetails[];
};

export type RawGondolasResponse = {
  results: RawGondola[];
  count: number;
};

export type CameraDetails = {
  id: string;
  thumbnail: string;
  imagePng: string;
  imageWebp: string;
  height: number;
  width: number;
  cameraType: CameraType;
};

export type Gondola = {
  id: number;
  name: string;
  cameras: CameraDetails[];
  shelfTrackingCameras: CameraDetails[];
  handTrackingCameras: CameraDetails[];
};

export type GondolasResponse = {
  results: Gondola[];
  count: number;
};

type GondolaSkuSectionsVariables = {
  gondolaId: string;
  search?: string;
  page?: number;
  limit?: number;
  ordering?: string;
  on_datetime?: string;
};

export type SkuSection = {
  id: number;
  sku_section_id: number;
  variant: {
    id: number;
    name: string;
    barcode: string;
    image: string;
    variant_id: string;
    uom: string;
    uom_internal: string;
  };
  polygon_points: [number, number][];
  original_image: string;
  compressed_image: string;
  width: number;
  height: number;
  // For Planogram Overlay
  color?: string;
};

export type GondolaSkuSectionsResponse = {
  results: Record<string, SkuSection>[];
  count: number;
};

export type Schedule = {
  id: number;
  description: string;
  from_datetime: string;
  to_datetime: string | null;
};

type SchedulesResponse = {
  results: Schedule[];
  count: number;
};
type SchedulesVariables = {
  search?: string;
  page?: number;
  limit?: number;
  ordering?: string;
  from_datetime?: string;
  to_datetime?: string;
};

export type ObservingCameraMask = {
  camera_local_id: string;
  masks: {
    [gondolaName: string]: [number, number][];
  };
};

export type PlanogramResponse = {
  result: {
    id: number;
    description: string;
    created_at: string;
    gondolas: {
      id: number;
      name: string;
      description: string;
      height: number;
      width: number;
      shelves: {
        id: number;
        description: string;
        height: number;
        width: number;
        depth: number;
        offset_from_gondola_top: number;
        order: number;
        bins: {
          id: number;
          description: string;
          width: number;
          depth: number;
          offset_from_gondola_top: number;
          order: number;
          x: number[];
          y: number[];
          z: number[];
          variant: {
            id: number;
            display_name: string;
            sku: string;
            rms_variant_id: string;
            image: string;
          };
        }[];
      }[];
      vertices: number[][] | null;
      faces: number[][] | null;
    }[];
    observing_cameras_masks: ObservingCameraMask[];
  };
};

type ZoneHistoryResponse = {
  results: {
    id: string;
    zone_type: number;
    description: string;
    zone_min_x: number;
    zone_min_y: number;
    zone_max_x: number;
    zone_max_y: number;
  }[];
  count: number;
};

type ZoneHistoryVariables = {
  historyId: number;
};

export type ZoneProjection = {
  id: string;
  description: string;
  projection: [number, number][];
};

type ZoneProjectionResponse = {
  result: ZoneProjection;
};

type ZoneProjectionVariables = {
  historyId: number;
  zoneId: string;
  cameraId: string;
  search?: string;
  page?: number;
  limit?: number;
  ordering?: string;
  on_datetime?: string;
};

export type CalibrationsResponse = {
  result: {
    id: number;
    number_of_cameras: number;
    description: string;
    created_at: string;
    camera_calibrations: CameraCalibration[];
  };
};

export type CameraCalibration = {
  camera_id: string;
  camera_type: string;
  location: [number, number, number];
  look_at: [number, number, number];
};

export type ProductVariant = {
  id: number;
  display_name: string;
  sku_barcode: string;
  image: string;
};

export type VariantsResponse = {
  results: ProductVariant[];
};

export type VariantsVariables = {
  sku?: string;
  sku_id?: string;
  search?: string;
  page?: number;
  page_size?: number;
  is_archived?: boolean;
};

export interface FrameAvailabilityQueryParams {
  frame_start_time: string;
  frame_end_time: string;
  camera_local_id: string;
}

export interface FrameAvailabilityResponse {
  result: {
    frame_found: boolean;
  };
}
